import { motion } from 'motion/react';
import { Container, Kicker, Mark } from '@/components/primitives';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { TrustStrip } from '@/components/TrustStrip';
import { Metrics } from '@/components/Metrics';
import { CTA } from '@/components/CTA';

const STORIES = [
  {
    code: 'CS-01',
    firm: 'Almacis',
    location: 'Pescara · IT',
    sector: 'Imprese generali',
    headline: 'Da oltre settanta fogli Excel a una sola sala operativa.',
    body: "Prima del pilota, il martedì di reporting in Almacis significava oltre settanta fogli Excel, altrettanti gruppi WhatsApp, altrettante chiamate da ricostruire a mano. Da quando il pilota è iniziato, il reporting è continuo — e il martedì torna a essere un giorno di lavoro.",
    facts: [
      { label: 'Cantieri', value: '70+' },
      { label: 'Pilota', value: 'In corso' },
      { label: 'Sede', value: 'Pescara' },
    ],
  },
];

export default function ClientiPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden pt-36 pb-20 md:pt-44 md:pb-24">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(40% 50% at 50% -10%, color-mix(in oklch, var(--primary) 14%, transparent), transparent 70%)',
          }}
        />
        <Container className="relative">
          <Badge variant="soft">· Clienti · Q1 2026</Badge>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="boss-display mt-6 max-w-[16ch] text-balance text-[56px] md:text-[104px]"
          >
            L'impresa che ha fatto <Mark>il salto.</Mark>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9 }}
            className="mt-7 max-w-2xl text-pretty text-[18px] leading-[1.55] text-fg-2"
          >
            Un pilota, oltre settanta cantieri attivi. Sono i numeri che possiamo mostrare oggi. Quelli che
            contano davvero — ore restituite ai geometri, sere a casa con i figli — sono nel racconto qui
            sotto.
          </motion.p>
        </Container>
      </section>

      <TrustStrip />

      {/* Stories */}
      <section className="relative py-24 md:py-32">
        <Container>
          <Kicker index="01" label="Storie dal campo" />
          <div className="mt-12 space-y-6">
            {STORIES.map((s, i) => (
              <motion.div
                key={s.code}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Card className="overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr]">
                    <div className="border-b border-border p-7 lg:border-b-0 lg:border-r">
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary">
                        {s.code}
                      </div>
                      <h3 className="boss-h1 mt-3 text-[30px]">{s.firm}</h3>
                      <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        {s.location} · {s.sector}
                      </div>
                      <dl className="mt-7 grid grid-cols-3 gap-3 border-t border-border pt-5">
                        {s.facts.map((f) => (
                          <div key={f.label}>
                            <dd className="text-[22px] font-light tracking-tight tabular-nums">{f.value}</dd>
                            <dt className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                              {f.label}
                            </dt>
                          </div>
                        ))}
                      </dl>
                    </div>
                    <div className="p-7">
                      <p className="boss-h1 text-balance text-[28px] leading-[1.15] md:text-[36px]">
                        {s.headline}
                      </p>
                      <p className="mt-5 text-pretty text-[15px] leading-[1.65] text-fg-2">{s.body}</p>
                      <Button asChild variant="link" className="mt-5 -ml-4">
                        <a href={`#${s.code.toLowerCase()}`}>Leggi il caso studio →</a>
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      <Metrics />
      <CTA />
    </>
  );
}
